import React from 'react';
import {StyleSheet, Text, type TextProps, type TextStyle} from 'react-native';
import {fonts} from '@/shared/ui/fonts';
import {useTheme} from '@/shared/ui/theme';

/**
 * Типографика — шкала дизайн-системы SamVision (`ui-kit/src/styles/tokens.css`):
 *
 *      xs 11   sm 12   base 13   md 14   lg 16   xl 18   2xl 22   3xl 28   display 40
 *
 * База — 13 px: таблица на 1366×768 должна показывать смену целиком, а не пять
 * строк крупным шрифтом. Служебный стиль — подписи полей, шапка таблицы, заголовки
 * панелей — 12 px строчными (`--caps-size`): у Hikvision он строчный, подпись
 * читается словом, а не аббревиатурой.
 *
 * ## Что это заменило, и почему
 *
 * Шкалу Tailwind с базой 14 и 24–36pt цифрами в гротеске. Цифры теперь — в
 * JetBrains Mono: показатель — приборная шкала, и там, где символы сверяют по
 * одному, ширина у всех должна быть одна (`metric` / `metricSm` / `mono`). Они и
 * меньше: 22 там, где было 30, — на плитке 150pt шириной 30pt число было большей
 * частью плитки.
 *
 * Вес выражается ВЫБОРОМ СЕМЕЙСТВА, никогда `fontWeight`: у Play два начертания,
 * обычное и жирное, и на Android `fontWeight: 700` заставляет ReactFontManager
 * искать `<family>_bold.ttf` и молча подставлять Roboto. См. fonts.ts.
 *
 * Варианты, и на какой ступени каждый стоит:
 *
 * - hero     — 28 mono. Одна цифра, ради которой существует экран. Редко.
 * - metric   — 22 mono medium tabular. ЧИСЛО, которое сообщает панель.
 * - metricSm — 18 mono medium. То же в три плитки в ряд.
 * - eyebrow  — 12 muted. Подпись над числом, шапка таблицы, имя группы.
 * - display  — 18 bold. Имя экрана.
 * - title    — 16 bold. Заголовок диалога, крупной секции.
 * - heading  — 14 bold. Заголовок карточки и раздела, ступенью ниже экрана.
 * - subtitle — 13 bold. Имя строки — то, что в списке читают первым.
 * - body     — 13. Всё, что человек читает как текст. Рабочая лошадь.
 * - label    — 12, вторичная краска. Подпись поля: мельче и тише значения, она
 *              называет поле, а не соперничает с тем, что в нём.
 * - caption  — 12 muted. Подсказки, время, вторая строка строки.
 * - micro    — 11 muted. Самая мелкая читаемая метка: текст метки, подпись вкладки.
 * - mono     — 12 mono tabular. Номера, коды, время в строке.
 * - monoLg   — 18 mono medium. Машинная цифра заголовком (часы).
 *
 * Разрядка везде ноль: приборный шрифт читается ровным, а номера и время сверяют
 * символ за символом.
 *
 * Text is never hardcoded here: callers pass it in (i18n keys resolve upstream).
 */
export type TextVariant =
  | 'hero'
  | 'metric'
  | 'metricSm'
  | 'eyebrow'
  | 'display'
  | 'title'
  | 'heading'
  | 'subtitle'
  | 'body'
  | 'label'
  | 'caption'
  | 'micro'
  | 'mono'
  | 'monoLg';

export type AppTextProps = TextProps & {
  variant?: TextVariant;
  /** Overrides the scheme-derived colour — pass a palette value, not a literal. */
  color?: string;
};

const styles = StyleSheet.create({
  hero: {
    fontSize: 28,
    fontFamily: fonts.mono.medium,
    fontVariant: ['tabular-nums'],
    lineHeight: 34,
  },
  metric: {
    fontSize: 22,
    fontFamily: fonts.mono.medium,
    fontVariant: ['tabular-nums'],
    lineHeight: 28,
  },
  metricSm: {
    fontSize: 18,
    fontFamily: fonts.mono.medium,
    fontVariant: ['tabular-nums'],
    lineHeight: 24,
  },
  eyebrow: {fontSize: 12, fontFamily: fonts.sans.regular, lineHeight: 16},
  display: {fontSize: 18, fontFamily: fonts.sans.bold, lineHeight: 24},
  title: {fontSize: 16, fontFamily: fonts.sans.semibold, lineHeight: 22},
  heading: {fontSize: 14, fontFamily: fonts.sans.semibold, lineHeight: 20},
  subtitle: {fontSize: 13, fontFamily: fonts.sans.semibold, lineHeight: 18},
  body: {fontSize: 13, fontFamily: fonts.sans.regular, lineHeight: 18},
  label: {fontSize: 12, fontFamily: fonts.sans.regular, lineHeight: 16},
  caption: {fontSize: 12, fontFamily: fonts.sans.regular, lineHeight: 16},
  micro: {fontSize: 11, fontFamily: fonts.sans.regular, lineHeight: 15},
  mono: {
    fontSize: 12,
    fontFamily: fonts.mono.regular,
    fontVariant: ['tabular-nums'],
    lineHeight: 16,
  },
  monoLg: {
    fontSize: 18,
    fontFamily: fonts.mono.medium,
    fontVariant: ['tabular-nums'],
    lineHeight: 24,
  },
});

/** Variants that are secondary by definition — muted unless the caller says otherwise. */
const MUTED: ReadonlySet<TextVariant> = new Set<TextVariant>([
  'caption',
  'micro',
  'eyebrow',
]);

/** Подпись поля — `--text-secondary`: тише значения, но не «выключена». */
const SECONDARY: ReadonlySet<TextVariant> = new Set<TextVariant>(['label']);

/**
 * What a TEXT LINK wears — pair it with `variant="label"` or `"body"`.
 *
 * Акцентом и без подчёркивания в покое: у ui-kit ссылка — это цвет, а подчёркивание
 * приходит на наведение. Наведения на телефоне нет, поэтому цвет и есть ссылка.
 */
export function useLinkText(): TextStyle {
  return {color: useTheme().primaryInk};
}

export function AppText({
  variant = 'body',
  color,
  style,
  ...rest
}: AppTextProps) {
  const p = useTheme();
  const base =
    color ??
    (MUTED.has(variant)
      ? p.mutedForeground
      : SECONDARY.has(variant)
      ? p.textSecondary
      : p.foreground);
  return <Text {...rest} style={[styles[variant], {color: base}, style]} />;
}
